import { useSyncExternalStore } from 'react';

function isSetStateCallback<Value>(value: Value | ((prev: Value) => Value)): value is (prev: Value) => Value {
    return typeof value === 'function';
}

export type HoneycombStoreSubscriber<Value> = (value: Value) => void;
type SetStateCallback<Value> = (value: Value) => Value;

export interface HoneycombStore<Value> {
    getState: () => Value;
    setState: (value: Value | SetStateCallback<Value>) => void;
    updateState: (value: Value | SetStateCallback<Value>) => void;
    initialize: (value: Value) => void;
    subscribe: (callback: HoneycombStoreSubscriber<Value>) => () => void;
}

export type HoneycombStoreValue<Store extends HoneycombStore<any>> = ReturnType<Store['getState']>;

export function createStore<Value extends Record<string, any>>(initialState: Value): HoneycombStore<Value> {
    let state = initialState;
    let initialized = false;
    const listeners = new Set<HoneycombStoreSubscriber<Value>>();

    return {
        getState() {
            return state;
        },

        updateState(value) {
            state = isSetStateCallback(value) ? value(state) : value;
        },

        setState(value) {
            this.updateState(value);
            listeners.forEach((listener) => listener(state));
        },

        initialize(value) {
            if (!initialized) {
                state = value;
                initialized = true;
            }
        },

        subscribe(callback) {
            listeners.add(callback);
            return () => listeners.delete(callback);
        },
    };
}

export function useStore<Store extends HoneycombStore<any>>(store: Store) {
    return useSyncExternalStore<HoneycombStoreValue<Store>>(
        store.subscribe,
        () => store.getState(),
        () => store.getState()
    );
}
